<!-- src/views/sources/users/index.vue -->
<script setup>
definePage({
  meta: { action: 'read', subject: 'source-user', navActiveLink: 'sources-users' },
})

import { getProjectId } from '@/utils/cdms'
import { useSnackBar } from '@/utils/stores/snackbar'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { VDataTable } from 'vuetify/labs/VDataTable'

/* ───────── 기본 ───────── */
const router = useRouter()
const snackbar = useSnackBar()
const projectLocked = ref(await isLockedProject())
const projectId = getProjectId()
const _projData = await getProjectData()
const projectStatus = ref(_projData.project_status || 'ready')
const isSiteNoEditable = computed(() => projectStatus.value === 'ready')
const getToday = () => new Date().toISOString().slice(0, 10)

/* ID 비교 유틸 */
const toKey  = v => String(v ?? '')
const sameId = (a, b) => toKey(a) === toKey(b)

/* ───────── 메인 사용자 표 ───────── */
const page = ref(1)
const itemsPerPage = ref(10)
const selected = ref([])
const search = ref('')
const users = ref([])


const filterItems = [
  { value: 'name',  title: '시험자' },
  { value: 'site_name', title: '수탁기관'   },
]

const filterItem = ref('name')

const headers = ref([
  { title: '시험자',   key: 'name',       width: '16%' },
  { title: '수탁기관', key: 'site_name',  width: '16%' },
  { title: '역할',     key: 'real_name',  width: '10%' },
  { title: '이메일',   key: 'email',      width: '22%' },
  { title: '시작일',   key: 'start_date', width: '12%' },
  { title: '종료일',   key: 'end_date',   width: '12%' },
  { title: '상태',     key: 'status',     width: '8%'  },
  { title: '편집',     key: 'actions',    width: '90px', sortable: false },
])

async function loadUserTable () {
  const { data } = await useApi(createUrl('/sources/users', { query: { projectId } }))

  users.value = Array.isArray(data.value) ? data.value : []
}

const expireItem = async ids => {
  const result = await $api(`/sources/users`, {
    method: 'DELETE',
    body: { ids },
  })
  selected.value = []
  await loadUserTable()
}
onMounted(() => {
  loadUserTable()
  loadSiteTable()
})

/* ───────── 탭 ───────── */
const activeTab = ref('users')

/* ───────── 기관 탭 ───────── */
const sitePage = ref(1)
const siteItemsPerPage = ref(10)
const siteSearch = ref('')
const siteList = ref([])

const siteHeaders = [
  { title: '수탁기관번호', key: 'site_no',       width: '15%' },
  { title: '수탁기관',     key: 'name',          width: '25%' },
  { title: '연락처',       key: 'phone',         width: '15%' },
  { title: '대표자',       key: 'investigator',  width: '15%' },
  { title: '시작일',       key: 'start_date',    width: '12%' },
  { title: '종료일',       key: 'end_date',      width: '12%' },
]

const siteFilterItems = [
  { value: 'name',    title: '수탁기관' },
  { value: 'site_no', title: '수탁기관번호' },
]
const siteFilterItem = ref('name')

const siteFilterText = (value, query, item) => {
  return value != null && query != null &&
    typeof value === 'string' &&
    String(item.columns[siteFilterItem.value] || '').toLowerCase().includes(String(query).toLowerCase())
}

async function loadSiteTable () {
  const { data } = await useApi(createUrl('/sources/sites', { query: { projectId } }))
  const arr = Array.isArray(data.value) ? data.value : []
  // 수탁기관번호 순 정렬
  arr.sort((a, b) => {
    const noA = a.site_no || a.siteNo || ''
    const noB = b.site_no || b.siteNo || ''
    return String(noA).localeCompare(String(noB), undefined, { numeric: true })
  })
  siteList.value = arr
}

/* ───────── 모달 상태 ───────── */
const isDialog = ref(false)
const saving = ref(false)
const q = ref('') // 모달 공통 검색
/** 모달 열 때 기존 기관의 원본 스냅샷 (변경 비교용) */
const siteOriginalSnapshot = ref(new Map())

// 좌: 기관(전체행) + 선택ID
// 각 row: { id(마스터 site id), projectSiteId(프로젝트-사이트 PK), ... }
const siteRows = ref([])          // [{id,name,phone,investigator,startDate,endDate,siteNo,projectSiteId,registered}]
const selectedSiteIds = ref([])

// 우: 사용자(기관 선택에 따라 로드) + 선택ID
const rawUsersForDialog = ref([]) // 병합된 서버 원본
const selectedUserIds = ref([])

/* 기존 연결: siteKey -> userKey[] */
const existingBySite = ref({})

/* ───────── 데이터 로딩 (기관) ───────── */
function mapSiteRow(s, registered=false) {
  // 프로젝트-사이트 PK (PATCH 경로에 사용). 백엔드는 project_sites_id 로 반환함.
  const projectSiteId =
    s.projectSiteId ??
    s.project_sites_id ??
    s.project_site_id ??
    s.psid ??
    (registered ? (s.projectSiteId ?? s.project_sites_id ?? s.project_site_id ?? s.id ?? null) : null)

  // 마스터 site id (POST나 비교용으로 사용)
  const siteId =
    s.siteId ??
    s.site_id ??
    s.site?.id ??
    s.id

  const startDate =
    s.startDate ??
    s.start_date ??
    s.contractDate ??
    s.contract_date ?? ''

  const endDate =
    s.endDate ??
    s.end_date ??
    s.contractEndDate ??
    s.contract_enddate ?? ''

  return {
    id: siteId,
    projectSiteId,
    type: s.type ?? s.type ?? '',
    name: s.name ?? s.site_name ?? '',
    phone: s.phone ?? s.tel ?? '',
    investigator: s.investigator ?? s.pi ?? '',
    startDate,
    endDate,
    siteNo: s.siteNo ?? s.site_no ?? null,
    registered,
  }
}
function dedupeByIdPreferExisting(list) {
  const map = new Map()
  for (const r of list) {
    const k = toKey(r.id)
    if (!k) continue
    const prev = map.get(k)
    if (!prev) map.set(k, r)
    else if (r.registered && !prev.registered) map.set(k, r)
  }
  
  return Array.from(map.values())
}
function remainingOrder(remainRows, existingRows) {
  const ex = new Set(existingRows.map(r => toKey(r.id)))
  
  return remainRows.filter(r => !ex.has(toKey(r.id)))
}

async function loadExistingAttachments () {
  const { data } = await useApi(createUrl('/sources/users', { query: { projectId } }))
  const arr = Array.isArray(data.value) ? data.value : []
  const tmp = {}
  for (const it of arr) {
    const sid = toKey(it.site_id ?? it.siteId ?? it.site?.id ?? it.site?.site_id)
    const uid = toKey(it.user_id ?? it.userId ?? it.user?.id ?? it.id)
    if (!sid || !uid) continue
    if (!tmp[sid]) tmp[sid] = []
    tmp[sid].push(uid)
  }
  for (const k in tmp) tmp[k] = Array.from(new Set(tmp[k]))
  existingBySite.value = tmp
}

async function loadSitesForDialog () {
  const { data: remainData }   = await useApi(createUrl('/sources/remain-sites', { query: { projectId } }))
  const { data: existingData } = await useApi(createUrl('/sources/sites',       { query: { projectId } }))

  const remain   = Array.isArray(remainData.value)   ? remainData.value   : []
  const existing = Array.isArray(existingData.value) ? existingData.value : []
 
  console.log(remain)

  console.log(existing)
  
  const remainRows   = remain.map(s => mapSiteRow(s, false))
  const existingRows = existing.map(s => mapSiteRow(s, true))

  // 등록된(site 등록됨) 우선 + 중복 제거
  let merged = dedupeByIdPreferExisting([
    ...remainingOrder(remainRows, existingRows),
    ...existingRows,
  ])

  console.log("merged,", merged)

  // 정렬: 등록됨(true) 먼저, 그 다음 이름
  merged.sort(
    (a, b) =>
      Number(b.registered) - Number(a.registered) ||
      String(a.name).localeCompare(String(b.name), 'ko'),
  )

  siteRows.value = merged

  // ✅ 초기 선택: 이미 등록된 사이트들만 선택(번호는 유지)
  selectedSiteIds.value = existingRows.map(r => r.id)
}

/* ───────── 사용자 로딩/병합 유틸 ───────── */
const isSelectableByStatus = status =>
  !/(expired|inactive|disabled|만료|비활성|탈퇴)/i.test(String(status ?? ''))

const userSiteKeys = u => {
  const ids = []
  if (u.site_id != null) ids.push(u.site_id)
  if (u.siteId  != null) ids.push(u.siteId)
  if (Array.isArray(u.siteIds)) ids.push(...u.siteIds)
  if (Array.isArray(u.sites))   ids.push(...u.sites.map(s => s.id))
  
  return [...new Set(ids.map(toKey))]
}

function existingUsersForSites (siteIds) {
  const chosen = new Set(siteIds.map(toKey))
  const acc = new Map()
  for (const r of users.value) {
    const sid = toKey(r.site_id ?? r.siteId ?? r.site?.id)
    if (!chosen.has(sid)) continue
    const uid = toKey(r.user_id ?? r.userId ?? r.user?.id ?? r.id)
    if (!uid) continue

    const name     = r.name  ?? r.user?.name  ?? ''
    const email    = r.email ?? r.user?.email ?? ''
    const role     = r.role  ?? 'Member'
    const roleId   = r.role_id ?? r.roleId ?? null
    const status   = r.status ?? '비활성'
    const siteName = r.site_name ?? r.site?.name ?? ''

    const cur = acc.get(uid) || {
      id: uid, name, email, role, roleId, status,
      siteIds: new Set(),
      siteNamesById: {},
    }

    cur.siteIds.add(sid)
    if (siteName) cur.siteNamesById[sid] = siteName
    acc.set(uid, cur)
  }
  
  return Array.from(acc.values()).map(u => ({
    ...u,
    siteIds: Array.from(u.siteIds),
  }))
}

function mergeUsersForDialog (remainList, existingList) {
  const map = new Map()
  for (const u of existingList) map.set(toKey(u.id), { ...u, _mergeExisting: true })
  for (const u of remainList) {
    const key = toKey(u.id)
    if (!map.has(key)) map.set(key, { ...u })
    else {
      const base = map.get(key)
      const union = new Set([...(base.siteIds || userSiteKeys(base)), ...userSiteKeys(u)])

      map.set(key, { ...base, ...u, siteIds: Array.from(union), roleId: (base.roleId ?? u.roleId ?? u.role_id ?? null) })
    }
  }
  
  return Array.from(map.values()).sort(
    (a, b) => Number(b._mergeExisting ? 1:0) - Number(a._mergeExisting ? 1:0)
          || String(a.name||'').localeCompare(String(b.name||''), 'ko'),
  )
}

async function loadUsersForDialogBySites (siteIds) {
  if (!siteIds.length) { rawUsersForDialog.value = []; selectedUserIds.value = []  

    return }

  const { data } = await useApi(createUrl('/sources/remain-users', { query: { projectId } }))
  const remain = Array.isArray(data.value) ? data.value : []
  const chosen = new Set(siteIds.map(toKey))

  const remainFiltered = remain.filter(u => {
    const uSites = userSiteKeys(u)
    if (!uSites.length) return false
    
    return uSites.some(id => chosen.has(id))
  })

  const exist = existingUsersForSites(siteIds)

  rawUsersForDialog.value = mergeUsersForDialog(remainFiltered, exist)

  const wantExisting = new Set()
  for (const sid of siteIds.map(toKey)) {
    const arr = existingBySite.value[sid] || []

    arr.forEach(uid => wantExisting.add(toKey(uid)))
  }
  selectedUserIds.value = rawUsersForDialog.value
    .filter(u => wantExisting.has(toKey(u.id)))
    .map(u => u.id)
}

/* ───────── 모달 열기/닫기 ───────── */
async function openDialog () {
  if (projectLocked.value) return
  await loadUserTable()
  await loadSitesForDialog()
  await loadExistingAttachments()
  await loadUsersForDialogBySites(selectedSiteIds.value)

  const snap = new Map()
  for (const row of siteRows.value) {
    if (row.registered && row.projectSiteId) {
      snap.set(String(row.projectSiteId), {
        startDate: row.startDate ?? '',
        endDate: row.endDate ?? '',
        siteNo: row.siteNo ?? null,
      })
    }
  }
  siteOriginalSnapshot.value = snap

  isDialog.value = true
}

function onBulkStartPicked (val) {
  const d = String(val ?? '').trim()
  if (!d || !selectedSiteIds.value.length) return
  const keys = new Set(selectedSiteIds.value.map(toKey))
  for (const row of siteRows.value) {
    if (keys.has(toKey(row.id))) row.startDate = d
  }
  snackbar.showSnackBar('선택한 기관에 시작일을 적용했습니다.')
}

function onBulkEndPicked (val) {
  const d = String(val ?? '').trim()
  if (!d || !selectedSiteIds.value.length) return
  const keys = new Set(selectedSiteIds.value.map(toKey))
  for (const row of siteRows.value) {
    if (keys.has(toKey(row.id))) row.endDate = d
  }
  snackbar.showSnackBar('선택한 기관에 종료일을 적용했습니다.')
}

const closeDialog = () => { isDialog.value = false }

// ─────────────────────────────────────────
// 다이얼로그 닫기: persistent 유지 + 수동 ESC/바깥클릭 처리
// ─────────────────────────────────────────
let clickStartedOnFlatpickr = false

const onDocPointerDown = (e) => {
  clickStartedOnFlatpickr = !!e.target.closest('.flatpickr-calendar')
}

const onDocKeyDown = (e) => {
  if (e.key !== 'Escape') return
  if (document.querySelector('.flatpickr-calendar.open')) return
  if (isDialog.value) { isDialog.value = false; return }
}

const onDialogOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isDialog.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown, true)
  document.addEventListener('keydown', onDocKeyDown, true)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true)
  document.removeEventListener('keydown', onDocKeyDown, true)
})

/* ───────── 파생/검색 ───────── */
const filteredSiteRows = computed(() => {
  const k = q.value.trim().toLowerCase()
  if (!k) return siteRows.value
  
  return siteRows.value.filter(s =>
    String(s.name||'').toLowerCase().includes(k) ||
    String(s.phone||'').toLowerCase().includes(k) ||
    String(s.investigator||'').toLowerCase().includes(k),
  )
})

const usersForDialog = computed(() => {
  const chosen = new Set(selectedSiteIds.value.map(toKey))
  
  return rawUsersForDialog.value.map(u => {
    const byStatus = isSelectableByStatus(u.status)
    const uSites   = userSiteKeys(u)
    const hasSite  = uSites.length > 0
    const inChosen = hasSite && uSites.some(id => chosen.has(id))
    const bySite   = hasSite && inChosen

    let siteName = u.siteName ?? u.site_name ?? u.site?.name ?? ''
    if (!siteName && u.siteNamesById) {
      for (const sid of uSites.map(toKey)) {
        if (chosen.has(sid) && u.siteNamesById[sid]) { siteName = u.siteNamesById[sid]; break }
      }
    }

    let already = false
    if (hasSite) {
      for (const sid of uSites.map(toKey)) {
        const arr = existingBySite.value[toKey(sid)] || []
        if (arr.includes(toKey(u.id)) && chosen.has(toKey(sid))) { already = true; break }
      }
    }

    return { ...u, siteName, selectable: byStatus && bySite, _hasSite: hasSite, _bySite: bySite, _already: already }
  })
})

const filteredUsersForDialog = computed(() => {
  const k = q.value.trim().toLowerCase()
  const base = (usersForDialog.value)
  if (!k) return base
  
  return base.filter(u =>
    String(u.name||'').toLowerCase().includes(k) ||
    String(u.email||'').toLowerCase().includes(k) ||
    String(u.role||'').toLowerCase().includes(k) ||
    String(u.siteName||'').toLowerCase().includes(k),
  )
})

const selectedSites = computed(() =>
  siteRows.value.filter(r => selectedSiteIds.value.some(x => sameId(x, r.id))),
)

/** 선택된 기관들 중 기관 번호(siteNo) 중복 여부. 수탁기관만 번호 검사, 프로젝트 내 기관 번호는 유일해야 함 */
const hasDuplicateSiteNo = computed(() => {
  const numbers = selectedSites.value
    .filter(s => s.type === 'investigator') // 수탁기관만 번호 검사
    .map(s => {
      const n = Number(s.siteNo)
      
      return (Number.isInteger(n) && n > 0) ? n : null
    })
    .filter(v => v !== null)

  
  return new Set(numbers).size !== numbers.length
})

const canSave = computed(() =>
  selectedSites.value.length > 0 && !hasDuplicateSiteNo.value,
)

/* ───────── siteNo 자동 배정 로직 ───────── */
function usedSiteNoSet () {
  const used = new Set()
  for (const id of selectedSiteIds.value) {
    const row = siteRows.value.find(r => sameId(r.id, id))
    if (!row) continue
    const n = Number(row.siteNo)
    if (Number.isInteger(n) && n > 0) used.add(n)
  }
  
  return used
}
function getNextSiteNo () {
  const used = usedSiteNoSet()
  let i = 1
  while (used.has(i)) i++
  
  return i
}

/* ───────── 선택 토글 ───────── */
const isSiteSelected = id => selectedSiteIds.value.some(x => sameId(x, id))

/** 기관 전체 선택/해제용: 필터된 행 기준 */
const allSitesSelected = computed(() => {
  const rows = filteredSiteRows.value
  if (!rows.length) return false

  const selectedSet = new Set(selectedSiteIds.value.map(toKey))

  return rows.every(r => selectedSet.has(toKey(r.id)))
})

const someSitesSelected = computed(() => {
  const rows = filteredSiteRows.value
  if (!rows.length) return false

  const selectedSet = new Set(selectedSiteIds.value.map(toKey))
  const count = rows.filter(r => selectedSet.has(toKey(r.id))).length

  return count > 0 && count < rows.length
})

function toggleAllSites () {
  const rows = filteredSiteRows.value
  if (!rows.length) return

  const selectedSet = new Set(selectedSiteIds.value.map(toKey))
  const allSelected = rows.every(r => selectedSet.has(toKey(r.id)))

  if (allSelected) {
    const toRemove = new Set(rows.map(r => toKey(r.id)))

    selectedSiteIds.value = selectedSiteIds.value.filter(id => !toRemove.has(toKey(id)))
    rows.forEach(r => {
      if (!r.registered) r.siteNo = null
    })
  } else {
    rows.forEach(r => {
      if (!selectedSet.has(toKey(r.id))) {
        selectedSiteIds.value.push(r.id)
        if ((r.siteNo == null || r.siteNo === '' || Number.isNaN(Number(r.siteNo))) && !r.registered && r.type === 'investigator') {
          r.siteNo = getNextSiteNo()
        }
      }
    })
  }
  nextTick(afterSitesChanged)
}

async function afterSitesChanged () {
  await loadUsersForDialogBySites(selectedSiteIds.value)
  pruneInvalidUserSelections()
}

function toggleSite(id) {
  const idx = selectedSiteIds.value.findIndex(x => sameId(x, id))
  const row = siteRows.value.find(r => sameId(r.id, id))
  if (!row) return

  if (idx >= 0) {
    // 선택 해제
    selectedSiteIds.value.splice(idx, 1)

    // ✅ 미등록(신규)만 번호를 비워서 재사용 가능하게
    if (!row.registered) row.siteNo = null
    nextTick(afterSitesChanged)
  } else {
    // 선택
    console.log(row)
    selectedSiteIds.value.push(id)

    // ✅ 수탁기관(investigator)만 자동 번호 부여, 그 외(sponsor/Etc 등)는 부여 안 함
    if ((row.siteNo == null || row.siteNo === '' || Number.isNaN(Number(row.siteNo))) && !row.registered && row.type === 'investigator') {
      row.siteNo = getNextSiteNo()
    }
    nextTick(afterSitesChanged)
  }
}

const isUserSelected = id => selectedUserIds.value.some(x => sameId(x, id))

/** 사용자 전체 선택/해제용: 필터된 행 중 selectable만 대상 */
const allUsersSelected = computed(() => {
  const selectable = filteredUsersForDialog.value.filter(u => u.selectable)
  if (!selectable.length) return false

  const selectedSet = new Set(selectedUserIds.value.map(toKey))

  return selectable.every(u => selectedSet.has(toKey(u.id)))
})

const someUsersSelected = computed(() => {
  const selectable = filteredUsersForDialog.value.filter(u => u.selectable)
  if (!selectable.length) return false

  const selectedSet = new Set(selectedUserIds.value.map(toKey))
  const count = selectable.filter(u => selectedSet.has(toKey(u.id))).length

  return count > 0 && count < selectable.length
})

function toggleAllUsers () {
  const selectable = filteredUsersForDialog.value.filter(u => u.selectable)
  if (!selectable.length) return

  const selectedSet = new Set(selectedUserIds.value.map(toKey))
  const allSelected = selectable.every(u => selectedSet.has(toKey(u.id)))

  if (allSelected) {
    const toRemove = new Set(selectable.map(u => toKey(u.id)))

    selectedUserIds.value = selectedUserIds.value.filter(id => !toRemove.has(toKey(id)))
  } else {
    selectable.forEach(u => {
      if (!selectedSet.has(toKey(u.id))) selectedUserIds.value.push(u.id)
    })
  }
}

function toggleUser(id, disabled=false) {
  if (disabled) return
  const idx = selectedUserIds.value.findIndex(x => sameId(x, id))
  if (idx >= 0) selectedUserIds.value.splice(idx, 1)
  else selectedUserIds.value.push(id)
}

/* 선택 불가 자동 해제 */
function pruneInvalidUserSelections () {
  const allow = new Set(filteredUsersForDialog.value.filter(u => u.selectable).map(u => toKey(u.id)))

  selectedUserIds.value = selectedUserIds.value.filter(id => allow.has(toKey(id)))
}
watch([selectedSiteIds, rawUsersForDialog], pruneInvalidUserSelections)

/* ───────── 저장(신규 추가 + 기존 수정 + 연결 해제 + 사이트 삭제) ───────── */
async function submitAll () {
  if (saving.value || !canSave.value) return
  saving.value = true
  try {
    const selectedKeys = new Set(selectedSiteIds.value.map(toKey))
    const initiallyRegisteredSites = siteRows.value.filter(s => s.registered)

    const removedSites = initiallyRegisteredSites.filter(s => !selectedKeys.has(toKey(s.id)))

    const removedSitesUserCount = removedSites.reduce(
      (sum, s) => sum + ((existingBySite.value[toKey(s.id)] || []).length),
      0,
    )

    /* 1) 신규 기관만 등록 (siteNo 포함, 수탁기관만 번호 부여 가능) */
    const newSites = selectedSites.value.filter(s => !s.registered)
    if (newSites.length) {
      await Promise.all(
        newSites.map(s => {
          const siteNo = (s.type === 'investigator' && s.siteNo !== '' && s.siteNo != null)
            ? Number(s.siteNo)
            : null

          
          return $api('/sources/sites', {
            method: 'POST',
            body: {
              site: {
                projectId,
                siteId: s.id,
                contractDate: s.startDate || null,
                endDate: s.endDate   || null,
                siteNo,
              },
            },
          })
        }),
      )
    }

    /* 1-β) 기존 기관 업데이트 — 변경된 기관만 PATCH */
    const existingSites = selectedSites.value.filter(s => s.registered && s.projectSiteId)
    const byProjectSiteId = new Map()
    for (const s of existingSites) {
      byProjectSiteId.set(String(s.projectSiteId), s)
    }
    for (const s of byProjectSiteId.values()) {
      const key = String(s.projectSiteId)
      const orig = siteOriginalSnapshot.value.get(key)

      const siteNo = (s.type === 'investigator' && s.siteNo !== '' && s.siteNo != null)
        ? Number(s.siteNo)
        : null

      const curStart = s.startDate ?? ''
      const curEnd = s.endDate ?? ''

      if (orig
        && orig.startDate === curStart
        && orig.endDate === curEnd
        && orig.siteNo === siteNo) {
        continue
      }

      await $api(`/sources/sites/${s.projectSiteId}`, {
        method: 'PATCH',
        body: {
          site: {
            siteId: s.id,
            contractDate: curStart || null,
            endDate: curEnd || null,
            siteNo,
          },
        },
      })
    }

    /* 2) 사용자 연결/해제 Diff */
    const picked = usersForDialog.value.filter(u =>
      selectedUserIds.value.some(x => sameId(x, u.id)),
    )

    const pickedById = Object.fromEntries(picked.map(u => [toKey(u.id), u]))

    const addRequests = []
    const delRequests = []

    for (const s of selectedSites.value) {
      const sidKey = toKey(s.id)

      const desired = new Set(
        picked
          .filter(u => (u.siteIds ? u.siteIds : userSiteKeys(u)).some(id => sameId(id, s.id)))
          .map(u => toKey(u.id)),
      )

      const existing = new Set((existingBySite.value[sidKey] || []).map(toKey))

      // Add
      for (const uidKey of desired) {
        if (!existing.has(uidKey)) {
          addRequests.push(
            $api('/sources/users', {
              method: 'POST',
              body: {
                projectId,
                data: {
                  userId: uidKey,
                  siteId: s.id,
                  roleId: pickedById[uidKey]?.roleId ?? pickedById[uidKey]?.role_id ?? null,
                  startDate: s.startDate || null,
                  endDate: s.endDate   || null,
                },
              },
            }),
          )
        }
      }

      // Delete
      for (const uidKey of existing) {
        if (!desired.has(uidKey)) {
          delRequests.push(
            $api('/sources/usersDestroy', {
              method: 'DELETE',
              body: { projectId, data: { userId: uidKey, siteId: s.id } },
            }),
          )
        }
      }
    }

    /* 3) 사이트 삭제 준비 */
    const siteDeleteRequests = removedSites.map(s =>
      $api('/sources/sites', { method: 'DELETE', body: { projectId, siteId: s.id } }),
    )

    /* 4) 안내 */
    const totalDetach = delRequests.length
    const totalSiteDelete = removedSites.length
    const needsConfirm = (totalDetach > 0) || (totalSiteDelete > 0)

    if (needsConfirm) {
      const parts = []
      if (totalSiteDelete) {
        parts.push(`제외된 기존 기관 ${totalSiteDelete}개 삭제`)
        if (removedSitesUserCount) parts.push(`(해당 기관에 묶인 사용자 ${removedSitesUserCount}명도 함께 삭제)`)
      }
      if (totalDetach) parts.push(`선택 해제된 사용자 연결 ${totalDetach}건 삭제`)
      const ok = window.confirm(`${parts.join(' · ')}\n진행하시겠습니까?`)
      if (!ok) {
        if (addRequests.length) await Promise.all(addRequests)
        snackbar.showSnackBar('추가 등록만 처리되었습니다.')
        await loadUserTable()
        await loadSiteTable()
        await loadExistingAttachments()
        closeDialog()
        saving.value = false

        return
      }
    }

    /* 5) 실행 순서 */
    if (siteDeleteRequests.length) await Promise.all(siteDeleteRequests)
    if (delRequests.length)       await Promise.all(delRequests)
    if (addRequests.length)       await Promise.all(addRequests)

    /* 6) 마무리 */
    await loadUserTable()
    await loadSiteTable()
    await loadExistingAttachments()
    snackbar.showSnackBar('처리가 완료되었습니다.')
    closeDialog()
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div>
    <h3 class="text-lg font-semibold mb-4">
      기관 및 사용자 등록
    </h3>

    <!-- 기관 / 시험자 탭 -->
    <VTabs v-model="activeTab" class="v-tabs-pill mb-4">
      <VTab value="sites">기관</VTab>
      <VTab value="users">시험자</VTab>
    </VTabs>

    <!-- ───────── 기관 탭 ───────── -->
    <VCard v-show="activeTab === 'sites'">
      <VCardText>
        <div class="toolbar">
          <div class="left" />
          <div class="right">
            <AppSelect
              v-model="siteFilterItem"
              :items="siteFilterItems"
              style="inline-size:150px"
            />
            <AppTextField
              v-model="siteSearch"
              class="pe-4"
              style="inline-size:200px"
              :placeholder="$t('Search') + '...'"
              density="compact"
            />
            <VBtn
              v-if="$can('write', 'source-user')"
              prepend-icon="tabler-plus"
              color="primary"
              :disabled="projectLocked"
              @click="openDialog"
            >
              기관 및 사용자 등록
            </VBtn>
          </div>
        </div>
      </VCardText>
      <VCardText>
        <VDataTable
          v-model:items-per-page="siteItemsPerPage"
          v-model:page="sitePage"
          :headers="siteHeaders"
          :items="siteList"
          :search="siteSearch"
          :custom-filter="siteFilterText"
          density="compact"
          class="custom-table"
        />
      </VCardText>
    </VCard>

    <!-- ───────── 시험자 탭 ───────── -->
    <VCard v-show="activeTab === 'users'">
      <VCardText>
        <div class="toolbar">
          <div class="left">
            <VBtn
              v-if="$can('write', 'source-user')"
              color="error"
              variant="tonal"
              :disabled="projectLocked || !selected.length"
              @click="expireItem(selected.map(s => s.id ?? s))"
            >
              비활성화
            </VBtn>
          </div>
          <div class="right">
            <AppSelect
              v-model="filterItem"
              :items="filterItems"
              style="inline-size:150px"
            />
            <AppTextField
              v-model="search"
              class="pe-4"
              style="inline-size:200px"
              :placeholder="$t('Search') + '...'"
              density="compact"
            />
            <VBtn
              v-if="$can('write', 'source-user')"
              prepend-icon="tabler-plus"
              color="primary"
              :disabled="projectLocked"
              @click="openDialog"
            >
              기관 및 사용자 등록
            </VBtn>
          </div>
        </div>
      </VCardText>

      <VCardText>
        <VDataTable
          v-model="selected"
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :items="users"
          :search="search"
          :custom-filter="(value, query, item) => value!=null && query!=null && typeof value==='string' && String(item.columns[filterItem]).toLowerCase().includes(String(query).toLowerCase())"
          density="compact"
          class="custom-table"
          show-select
          item-selectable="selectable"
          @update:options="loadUserTable"
        >
          <template #item.name="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.name }}</span>
          </template>
          <template #item.site_name="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.site_name }}</span>
          </template>
          <template #item.real_name="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.real_name }}</span>
          </template>
          <template #item.email="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.email }}</span>
          </template>
          <template #item.start_date="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.start_date }}</span>
          </template>
          <template #item.end_date="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.end_date }}</span>
          </template>
          <template #item.status="{ item }">
            <span :class="{ deleted: item.status !== 'Active' }">{{ item.status === 'Active' ? '활성' : '비활성' }}</span>
          </template>


          <template #item.actions="{ item }">
            <div class="d-flex gap-1">
              <IconBtn
                v-if="$can('write', 'source-user')"
                :disabled="projectLocked"
                @click="$router.push({ name:'sources-users-edit-id', params:{ id:item.id } })"
              >
                <VIcon icon="tabler-edit" />
                <VTooltip
                  activator="parent"
                  location="top"
                >
                  수정
                </VTooltip>
              </IconBtn>
              <IconBtn
                v-if="item.status=='Active' && $can('write', 'source-user')"
                :disabled="projectLocked"
                @click="expireItem([item.id])"
              >
                <VIcon
                  icon="tabler-clock-cancel"
                  color="error"
                />
                <VTooltip
                  activator="parent"
                  location="top"
                >
                  비활성
                </VTooltip>
              </IconBtn>
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ───────── 모달 ───────── -->
    <VDialog
      v-model="isDialog"
      max-width="1280"
      persistent
      no-click-animation
      @click:outside="onDialogOutsideClick"
    >
      <VCard class="dlg">
        <div class="dlg-head">
          <div class="head-left">
            <span class="dlg-title">기관 및 사용자 등록</span>
          </div>
          <div class="head-right">
            <AppTextField
              v-model="q"
              placeholder="검색(기관/사용자/이메일/역할)"
              density="comfortable"
              hide-details
              style="inline-size: 300px"
              prepend-inner-icon="tabler-search"
            />
          </div>
        </div>

        <VCardText class="dlg-body">
          <!-- 좌: 기관 -->
          <div class="col">
            <div class="col-title">
              기관
            </div>

            <div class="list">
              <div class="list-head sites">
                <div class="c c-chk">
                  <VCheckbox
                    :model-value="allSitesSelected"
                    :indeterminate="someSitesSelected"
                    density="compact"
                    hide-details
                    @update:model-value="toggleAllSites"
                  />
                </div>
                <div class="c c-order">
                  기관 번호
                </div>
                <div class="c c-name">
                  기관
                </div>
                <div class="c c-owner">
                  책임자
                </div>
                <div class="c c-date c-date-head">
                  <span class="c-date-head__text">시작일</span>
                  <AppIconDatePicker
                    :disabled="!selectedSiteIds.length"
                    :title="selectedSiteIds.length ? '선택한 기관에 시작일 일괄 적용' : '기관을 먼저 선택하세요'"
                    @update:model-value="onBulkStartPicked"
                  />
                </div>
                <div class="c c-date c-date-head">
                  <span class="c-date-head__text">종료일</span>
                  <AppIconDatePicker
                    :disabled="!selectedSiteIds.length"
                    :title="selectedSiteIds.length ? '선택한 기관에 종료일 일괄 적용' : '기관을 먼저 선택하세요'"
                    @update:model-value="onBulkEndPicked"
                  />
                </div>
              </div>

              <div class="list-body">
                <div
                  v-for="row in filteredSiteRows"
                  :key="row.id"
                  class="list-row sites"
                  :class="{ selected: isSiteSelected(row.id) }"
                  @click="toggleSite(row.id)"
                >
                  <div
                    class="c c-chk"
                    @click.stop
                  >
                    <VCheckbox
                      :model-value="isSiteSelected(row.id)"
                      density="compact"
                      hide-details
                      @update:model-value="() => toggleSite(row.id)"
                    />
                  </div>

                  <!-- ✅ 순서 입력: 수탁기관(investigator)만 입력 가능, 그 외는 번호 부여 불가 -->
                  <div
                    class="c c-order"
                    @click.stop
                  >
                    <AppTextField
                      v-model="row.siteNo"
                      type="number"
                      min="1"
                      step="1"
                      :readonly="row.type !== 'investigator' || (!isSiteNoEditable && row.registered)"
                      hide-details="auto"
                      density="compact"
                      class="compact-field order-field"
                      :placeholder="row.type === 'investigator' ? '' : '-'"
                    />
                  </div>

                  <div class="c c-name">
                    <div class="primary-text">
                      {{ row.name }}
                      <span
                        v-if="row.registered"
                        class="tag-registered"
                      >이미 등록</span>
                    </div>
                    <div class="sub-text">
                      {{ row.phone }}
                    </div>
                  </div>

                  <div class="c c-owner">
                    <span class="sub-text">{{ row.investigator }}</span>
                  </div>

                  <div
                    class="c c-date"
                    @click.stop
                  >
                    <div class="date-field-wrapper">
                      <AppDateTimePicker
                        v-model="row.startDate"
                        append-inner-icon="tabler-calendar-event"
                        class="compact-field"
                        hide-details="auto"
                      />
                    </div>
                  </div>

                  <div
                    class="c c-date"
                    @click.stop
                  >
                    <div class="date-field-wrapper gap-2">
                      <AppDateTimePicker
                        v-model="row.endDate"
                        append-inner-icon="tabler-calendar-event"
                        class="compact-field"
                        hide-details="auto"
                      />
                      <IconBtn
                        size="small"
                        @click="row.endDate = getToday()"
                      >
                        <VIcon icon="tabler-calendar-check" />
                        <VTooltip
                          activator="parent"
                          location="top"
                        >
                          종료일을 오늘로 적용
                        </VTooltip>
                      </IconBtn>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!filteredSiteRows.length"
                  class="empty-hint"
                >
                  등록 가능한 기관이 없습니다.
                </div>
              </div>
            </div>
          </div>

          <!-- 우: 사용자(기관 선택 시에만 표시) -->
          <div
            v-if="selectedSiteIds.length"
            class="col"
          >
            <div class="col-title">
              사용자
            </div>
            <div class="list">
              <div class="list-head users">
                <div class="c c-chk">
                  <VCheckbox
                    :model-value="allUsersSelected"
                    :indeterminate="someUsersSelected"
                    density="compact"
                    hide-details
                    @update:model-value="toggleAllUsers"
                  />
                </div>
                <div class="c c-name">
                  사용자
                </div>
                <div class="c c-name">
                  기관
                </div>
              </div>

              <div class="list-body">
                <div
                  v-for="u in filteredUsersForDialog"
                  :key="u.id"
                  class="list-row users"
                  :class="{ disabled: !u.selectable, selected: isUserSelected(u.id) }"
                  @click="toggleUser(u.id, !u.selectable)"
                >
                  <div
                    class="c c-chk"
                    @click.stop
                  >
                    <VCheckbox
                      :model-value="isUserSelected(u.id)"
                      :disabled="!u.selectable"
                      density="compact"
                      hide-details
                      @update:model-value="() => toggleUser(u.id, !u.selectable)"
                    />
                  </div>

                  <div class="c c-name">
                    <div
                      class="primary-text"
                      :class="{ muted: !u.selectable }"
                    >
                      {{ u.name }}
                      <span
                        v-if="u._already"
                        class="tag-registered"
                      >이미 등록</span>
                    </div>
                    <div
                      v-if="!u._hasSite"
                      class="hint-badge"
                    >
                      소속없음
                    </div>
                  </div>

                  <div class="c c-email">
                    <span class="sub-text">{{ u.siteName }}</span>
                  </div>
                </div>

                <div
                  v-if="selectedSiteIds.length && !filteredUsersForDialog.length"
                  class="empty-hint"
                >
                  선택한 기관에 연결 가능한 사용자가 없습니다.
                </div>
              </div>
            </div>
          </div>

          <div
            v-else
            class="col"
          >
            <div
              class="list"
              style="display:flex;align-items:center;justify-content:center;min-block-size:200px"
            >
              <div class="empty-hint">
                좌측에서 기관을 선택하면 관련 사용자가 표시됩니다.
              </div>
            </div>
          </div>
        </VCardText>

        <div
          class="summary-bar"
          :class="{ empty: !selectedSites.length && !selectedUserIds.length, 'summary-bar--error': hasDuplicateSiteNo }"
        >
          <VIcon
            v-if="hasDuplicateSiteNo"
            icon="tabler-alert-circle"
            size="18"
            class="me-1"
          />
          <VIcon
            v-else-if="!selectedSites.length && !selectedUserIds.length"
            icon="tabler-info-circle"
            size="18"
            class="me-1"
          />
          <span
            v-if="hasDuplicateSiteNo"
            class="summary-bar__dup-msg"
          >
            기관 번호가 중복됩니다. 프로젝트 내에서 기관 번호는 서로 달라야 합니다.
          </span>
          <span v-else>
            {{ `선택된 기관 ${selectedSites.length}개 · 선택된 사용자 ${selectedUserIds.length}명` }}
          </span>
        </div>

        <VCardActions class="dlg-actions">
          <VBtn
            variant="tonal"
            color="secondary"
            @click="closeDialog"
          >
            취소
          </VBtn>
          <VBtn
            color="#3857F5"
            :disabled="!canSave"
            :loading="saving"
            @click="submitAll"
          >
            등록
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
/* 탭 Pill */
.v-tabs.v-tabs-pill {
  .v-slide-group-item--active.v-tab--selected {
    background-color: #6A78D3;
    color: white;
    font-weight: bold;
  }
}

.v-tabs.v-tabs-pill .v-tab.v-btn{
  border : 1px solid #6A78D3;
  color: #2F45B8;
  margin-inline-end: 10px;
}

/* 상단/메인 테이블 */
.toolbar{display:flex;justify-content:space-between;gap:12px}
.right{display:flex;align-items:center;gap:8px}
.deleted{text-decoration:line-through}

:deep(.custom-table .v-table__wrapper table){
  th{background:#F3F5F8!important;border-block-end:1px solid #E9EDF2;color:#7E8B97!important;font-size:13px;font-weight:600;padding-block:10px;padding-inline:12px}
  td{border-block-end:1px solid #EDF1F4;color:#344054;font-size:14px;padding-block:8px;padding-inline:12px}
}

/* 모달 */
.dlg{overflow:hidden;border-radius:12px}
.dlg-head{display:flex;align-items:center;justify-content:space-between;border-block-end:1px solid #EEF2F7;padding-block:14px;padding-inline:16px}
.dlg-title{color:#111827;font-size:16px;font-weight:700}
.dlg-body{display:grid;padding:16px;gap:16px;grid-template-columns:2fr 1fr}
.col{display:flex;flex-direction:column;min-inline-size:0}
.col-title{color:#111827;font-weight:700;margin-block-end:8px}

/* 리스트 헤더: 시작/종료 + 일괄 아이콘 달력 */
.c-date-head{display:flex;align-items:center;justify-content:space-between;min-inline-size:0}
.c-date-head__text{flex:1;min-inline-size:0;}

/* 리스트 */
.list{overflow:hidden;border:1px solid #E8ECEF;border-radius:10px;background:#fff}
.list-head{position:sticky;z-index:1;display:grid;align-items:center;background:#F7F9FB;border-block-end:1px solid #E9EDF2;color:#7E8B97;font-size:12.5px;font-weight:700;gap:8px;inset-block-start:0;padding-block:10px;padding-inline:12px}
.list-body{overflow:auto;max-block-size:420px}
.list-row{display:grid;align-items:center;border-block-end:1px solid #F1F4F7;gap:8px;padding-block:10px;padding-inline:12px;transition:background .15s}
.list-row:last-child{border-block-end:none}
.list-row:hover{background:#FAFBFD}
.list-row.selected{background:#EEF6FF}
.list-row.disabled{opacity:.55}

/* 열 구성(좌측 기관) - 시작일/종료일 컬럼에 min 너비 확보 */
.list-head.sites, .list-row.sites{
  grid-template-columns: 30px 80px 1.4fr 0.5fr minmax(140px, 1fr) minmax(140px, 1.2fr); /* ✅ 순서+시작일+종료일 */
}
.c.c-order :deep(.v-field){min-inline-size:72px}

/* 우측 사용자 리스트 */
.list-head.users, .list-row.users{
  grid-template-columns:36px 1.2fr 1.4fr;
}

/* 텍스트/배지 */
.c{min-inline-size:0}
.c.c-date{min-inline-size:0;overflow:hidden}
.c.c-date :deep(.v-field){justify-content:center;min-inline-size:140px;max-inline-size:100%}
.date-field-wrapper{display:flex;align-items:center;min-inline-size:0}
.date-field-wrapper.gap-2{gap:8px}
.order-field:deep(.v-field){border-radius:8px !important;min-block-size:36px !important}
.order-field:deep(input){text-align:center}

.primary-text{color:#111827;font-weight:600;line-height:1.2}
.sub-text{color:#8A94A6;font-size:12px}
.muted{color:#A0A8B5}
.badge-role{display:inline-block;border-radius:999px;background:#EEF2FF;color:#1F3AA3;font-size:12px;font-weight:700;padding-block:2px;padding-inline:8px}
.tag-registered{display:inline-block;border-radius:6px;background:#FFF7E6;color:#8A5A00;font-size:11px;font-weight:700;margin-inline-start:6px;padding-block:2px;padding-inline:6px;vertical-align:top}
.hint-badge{display:inline-block;border-radius:6px;background:#F1F5F9;color:#64748b;font-size:11px;font-weight:600;margin-inline-start:6px;padding-block:2px;padding-inline:6px}

.inline-flex{display:inline-flex;align-items:center}

/* 날짜 입력 컴팩트 */
.compact-field:deep(.v-field){border-radius:8px !important;min-block-size:36px !important}
.compact-field:deep(.v-field__input){padding-block:6px !important;font-size:13px !important}
.c.c-date :deep(.v-field__input input){font-size:13px !important}

/* 선택 요약 바 */
.summary-bar{
  display:flex;align-items:center;background:#F8FAFD;border-block-start:1px solid #EEF2F7;
  color:#344054;font-weight:600;gap:6px;padding-block:10px;padding-inline:16px;
}
.summary-bar.empty{color:#7E8B97;font-weight:500}
.summary-bar--error{background:#FEF2F2;color:#B91C1C;font-weight:600}
.summary-bar__dup-msg{flex:1}

/* 풋터 */
.dlg-actions{display:flex;justify-content:flex-end;gap:8px;padding-block:12px;padding-inline:16px}

/* 반응형 */
@media (max-width:1024px){.dlg-body{grid-template-columns:1fr}}
</style>
